import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { RiGooglePlayFill } from "react-icons/ri";
import logoDark from "../assets/images/dark font logo.png";
import logoLight from "../assets/images/light font logo.png";
import Sakhee from "../assets/apk/Sakhee.apk";

const navLinks = [
  { label: "Home", to: "/" },
  { label: "Products", to: "/products" },
  { label: "About Us", to: "/about" },
  { label: "Networks", to: "/network" },
  { label: "CSR", to: "/csr" },
  { label: "Reports & Disclosures", to: "/reports" },
  { label: "Grievance & Support", to: "/contact" },
];

const Navbar = ({ isScrolled }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMenuOpen]);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  const linkColorClasses = isScrolled
    ? "text-dark hover:text-primary"
    : "text-white hover:text-white/80";

  const ctaClasses = isScrolled
    ? "hidden lg:inline-flex items-center rounded-full bg-red text-white px-3.5 py-1.5 text-[13px] font-semibold shadow-lg transition duration-300 hover:-translate-y-0.5 hover:bg-red/90"
    : "hidden lg:inline-flex items-center rounded-full border border-white text-white px-3.5 py-1.5 text-[13px] font-semibold transition duration-300 hover:-translate-y-0.5 hover:bg-white/10 hover:text-white";

  const navShellClasses = isScrolled
    ? "bg-white/75 text-black shadow-[0_10px_50px_rgba(0,0,0,0.12)] border-b border-black/5"
    : "bg-black/30 text-white border border-white/10";

  const toggleMenu = () => setIsMenuOpen((prev) => !prev);
  const closeMenu = () => setIsMenuOpen(false);

  return (
    <div className="relative z-[320]">
      <nav
        className={`w-full backdrop-blur-xl transition-all duration-500 ${navShellClasses}`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative flex items-center gap-3 py-3 justify-center lg:justify-between">
            <Link
              to="/"
              className="flex items-center gap-3 mx-auto lg:mx-0"
              style={{
                transform: isScrolled ? "translateX(5%)" : "translateX(-25%)",
              }}
            >
              <img
                src={isScrolled ? logoDark : logoLight}
                alt="BSS Sonata logo"
                className="h-10 w-auto object-contain transition-transform duration-300"
                style={{
                  transform: isScrolled ? "scale(1.1)" : "scale(1.5)",
                  transformOrigin: "left center",
                }}
              />
              <span className="sr-only">BSS Sonata</span>
            </Link>

            <div className="hidden lg:flex items-center gap-2 ml-auto">
              {navLinks.map((link) => {
                const isActive = location.pathname === link.to;
                return (
                  <Link
                    key={link.to}
                    to={link.to}
                    className={`px-3 py-1.5 rounded-full text-[13px] transition-colors duration-300 ${linkColorClasses} ${
                      isActive
                        ? `${
                            isScrolled ? "text-primary" : "text-white"
                          } font-semibold`
                        : "font-normal"
                    }`}
                  >
                    {link.label}
                  </Link>
                );
              })}
            </div>

            <div className="flex items-center gap-3">
              <a
                //href={Sakhee}
                href="https://play.google.com/store/apps/details?id=com.arthsetu_app"
                target="_blank"
                rel="noreferrer"
                className={ctaClasses}
              >
                <span className="flex items-center gap-2">
                  <RiGooglePlayFill className="text-lg" />
                  <span>Sakhi App</span>
                </span>
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.bssmfi.shakti"
                target="_blank"
                rel="noreferrer"
                className={ctaClasses}
              >
                <span className="flex items-center gap-2">
                  <RiGooglePlayFill className="text-lg" />
                  <span>Shakti App</span>
                </span>
              </a>

              <button
                type="button"
                onClick={toggleMenu}
                aria-label="Toggle navigation menu"
                aria-expanded={isMenuOpen}
                className={`lg:hidden absolute right-0 inline-flex items-center justify-center w-10 h-10 rounded-full border transition duration-300 ${
                  isScrolled
                    ? "bg-white text-black border-black/10 hover:bg-white/80"
                    : "bg-white/10 text-white border-white/20 hover:bg-white/20"
                }`}
              >
                {isMenuOpen ? <X size={22} /> : <Menu size={22} />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div
        className={`fixed inset-0 z-[350] bg-black/60 backdrop-blur-[2px] transition-opacity duration-300 ${
          isMenuOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={closeMenu}
      />

      <div
        className={`fixed inset-y-0 right-0 z-[350] w-[30vw] min-w-[240px] max-w-[360px] transform transition-transform duration-300 ${
          isMenuOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex h-full flex-col bg-white/10 backdrop-blur-2xl border-l border-white/20 shadow-[0_20px_80px_rgba(0,0,0,0.45)] text-white">
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/15">
            <span className="text-xs font-semibold uppercase tracking-[0.35em]">
              Menu
            </span>
            <button
              type="button"
              onClick={closeMenu}
              aria-label="Close navigation menu"
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/20 hover:bg-white/10 transition"
            >
              <X size={18} />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto px-5 py-6 space-y-2">
            {navLinks.map((link) => {
              const isActive = location.pathname === link.to;
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={closeMenu}
                  className={`block rounded-xl px-4 py-3 text-base font-semibold transition-colors ${
                    isActive ? "bg-white/15 text-white" : "hover:bg-white/10"
                  }`}
                >
                  {link.label}
                </Link>
              );
            })}
            <a
              href={Sakhee}
              target="_blank"
              rel="noreferrer"
              className="tex-center mt-2"
            >
              <span className="flex mt-3 items-center gap-2 lg:inline-flex rounded-full bg-red px-3 p-1 justify-center">
                <RiGooglePlayFill className="text-lg" />
                <span>Sakhi App</span>
              </span>
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=com.bssmfi.shakti"
              target="_blank"
              rel="noreferrer"
              className="mt-5"
            >
              <span className="mt-5 flex items-center gap-2 lg:inline-flex rounded-full bg-red px-3 p-1 justify-center">
                <RiGooglePlayFill className="text-lg" />
                <span>Shakti App</span>
              </span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Navbar;
